import { getModelConfig } from '../model-configs.js';

// These CoreML builds have no VAE encoder; Socket rejects their guide jobs.
const GUIDE_UNSUPPORTED_IDS = new Set([
  'moss_image_sft_int8_convrot',
  'moss_image_sft_dmd8_int8_convrot',
  'coreml-small-stable-diffusion-v0', 'coreml-stable-diffusion-xl-base',
  'coreml-stable-diffusion-mixed-bit-palettization',
  'coreml-stable-diffusion-3-medium', 'coreml-stable-diffusion-3-medium-1024-t5'
]);
// The SDK and Socket permit this larger envelope when no tier range narrows it.
const EXTENDED_SIZE_IDS = new Set([
  'qwen_image_2512_fp8', 'qwen_image_2512_fp8_lightning',
  'qwen_image_edit_2511_fp8', 'qwen_image_edit_2511_fp8_lightning'
]);

function fail(model, message) {
  const error = new Error(`${model.label || model.name || model.id}: ${message}`);
  error.code = 'INVALID_MODEL_INPUT';
  error.modelId = model.id;
  throw error;
}

function numericValue(model, field, value, range) {
  const number = Number(value);
  const step = range.step || (range.decimals ? 10 ** -range.decimals : 1);
  if (!Number.isFinite(number) || number < range.min || number > range.max
    || Math.abs((number - range.min) / step - Math.round((number - range.min) / step)) > 1e-6) {
    fail(model, `${field} must be ${range.min}–${range.max} in increments of ${step}.`);
  }
  return number;
}

function dimensionValue(model, axis, value, range) {
  const number = Number(value);
  if (!Number.isInteger(number) || number < range.min || number > range.max
    || (range.step > 1 && number % range.step !== 0)) {
    fail(model, `${axis} must be ${range.min}–${range.max}px${range.step > 1 ? `, in multiples of ${range.step}` : ''}.`);
  }
  return number;
}

function selectOption(model, field, value, options) {
  if (!options?.allowed?.length) return undefined;
  if (!options.allowed.includes(value)) fail(model, `choose a supported ${field}.`);
  return value;
}

export function getImageInputCapabilities(model) {
  const parameters = model?.parameters || {};
  const supportsReferences = parameters.requiresContextImage === true
    || Number(parameters.maxContextImages) > 0 || (parameters.isExternalAPI && parameters.vendor === 'openai');
  const maxReferences = parameters.maxContextImages
    || (parameters.isExternalAPI ? 16 : /krea2_identity_edit/.test(model?.id || '') ? 2 : 3);
  // A worker model whose references are optional (FLUX 3 Image Klein) also renders
  // from a guide image alone, so it takes either input, one at a time.
  const optionalReferences = supportsReferences && parameters.requiresContextImage !== true && !parameters.isExternalAPI;
  return {
    supportsReferences, maxReferences,
    requiresReferences: parameters.requiresContextImage === true,
    supportsGuide: (!supportsReferences || optionalReferences) && !GUIDE_UNSUPPORTED_IDS.has(model?.id),
    // Socket requires Schnell denoising >= 0.01, i.e. influence <= 0.99.
    maxGuideStrength: model?.id === 'flux1-schnell-fp8' ? 0.99 : 1
  };
}

export function resolveImageDimensions(model, { width, height, sizePresets = [] } = {}) {
  const parameters = model.parameters || {};
  const defaultSize = parameters.defaultSize || sizePresets.find((entry) => entry.width === entry.height)?.width || 1024;
  const fallbackRange = { min: 256, max: EXTENDED_SIZE_IDS.has(model.id) ? 2560 : 2048, step: 8 };
  const constraints = parameters.sizeConstraints;
  const dimensions = {
    width: dimensionValue(model, 'Width', width ?? defaultSize, constraints
      ? { min: 256, max: constraints.maxEdge, step: constraints.multiple } : parameters.width || fallbackRange),
    height: dimensionValue(model, 'Height', height ?? defaultSize, constraints
      ? { min: 256, max: constraints.maxEdge, step: constraints.multiple } : parameters.height || fallbackRange)
  };
  if (constraints) {
    const pixels = dimensions.width * dimensions.height;
    const aspect = Math.max(dimensions.width / dimensions.height, dimensions.height / dimensions.width);
    if (pixels < constraints.minPixels || pixels > constraints.maxPixels || aspect > constraints.maxAspectRatio) {
      fail(model, `choose a size between ${constraints.minPixels.toLocaleString()} and ${constraints.maxPixels.toLocaleString()} pixels with an aspect ratio at most ${constraints.maxAspectRatio}:1.`);
    }
  }
  return dimensions;
}

export function getImageSizeErrors(models, dimensions) {
  return models.flatMap(model => {
    try {
      resolveImageDimensions(model, dimensions);
      return [];
    } catch (error) {
      return [{ modelId: model.id, message: error.message }];
    }
  });
}

/** Resolve one concrete request fragment; no model, quality or input fallback. */
export function resolveImageModelParameters(model, {
  width, height, network = 'fast', preset = 'final', modelOverrides = {},
  disableNSFWFilter = false, referenceAssets = [], guideImage = null, guideStrength = 0.5,
  modelOptions, sizePresets = []
} = {}) {
  if (!model?.parameters || !modelOptions?.steps) {
    const error = new Error('Model settings are unavailable. Refresh the catalog and retry.');
    error.code = 'MODEL_METADATA_UNAVAILABLE';
    throw error;
  }
  const parameters = model.parameters;
  const verified = getModelConfig(model.id);
  const overrides = modelOverrides || {};
  const result = {
    type: 'image', modelId: model.id, network, sizePreset: 'custom',
    ...resolveImageDimensions(model, { width, height, sizePresets }),
    outputFormat: overrides.outputFormat || 'png',
    numberOfPreviews: parameters.isExternalAPI ? 0 : 4,
    disableNSFWFilter: Boolean(disableNSFWFilter)
  };
  const formats = (parameters.outputFormats || ['png', 'jpeg', 'webp']).map((format) => format === 'jpeg' ? 'jpg' : format);
  if (!formats.includes(result.outputFormat)) fail(model, 'choose a supported output format.');
  if (model.requiresMatureContent && !disableNSFWFilter) fail(model, 'enable Mature Content before generating.');

  result.steps = numericValue(model, 'Steps', overrides.steps
    ?? (preset === 'draft' ? modelOptions.steps.min : verified.steps ?? modelOptions.steps.default), modelOptions.steps);
  if (modelOptions.guidance) {
    result.guidance = numericValue(model, 'Guidance', overrides.guidance ?? verified.guidance ?? modelOptions.guidance.default, modelOptions.guidance);
  }
  if (!parameters.isExternalAPI) {
    result.negativePrompt = overrides.negativePrompt ?? verified.negativePrompt ?? parameters.negativePrompt?.default ?? '';
    for (const key of ['sampler', 'scheduler', 'vae']) {
      const value = selectOption(model, key, overrides[key] ?? verified[key] ?? modelOptions[key]?.default, modelOptions[key]);
      if (value !== undefined && value !== null) result[key] = value;
    }
  } else {
    const quality = overrides.gptImageQuality || 'medium';
    if (!parameters.qualities?.includes(quality) || quality === 'auto') fail(model, 'choose an explicit supported image quality.');
    result.gptImageQuality = quality;
    result.gptImageBackground = overrides.gptImageBackground || 'opaque';
    if (!parameters.backgrounds?.includes(result.gptImageBackground)) fail(model, 'choose a supported background.');
    if (result.gptImageBackground === 'transparent' && result.outputFormat === 'jpg') fail(model, 'transparent output requires PNG or WebP.');
  }

  const capabilities = getImageInputCapabilities(model);
  const activeReferences = referenceAssets.filter((asset) => asset.enabled !== false);
  if (capabilities.requiresReferences && !activeReferences.length) fail(model, 'add at least one reference image before generating.');
  if (activeReferences.length && !capabilities.supportsReferences) fail(model, 'reference images require an editing model. Remove them or use a guide image with this model.');
  if (activeReferences.length > capabilities.maxReferences) fail(model, `use at most ${capabilities.maxReferences} reference images; none will be dropped automatically.`);
  if (activeReferences.length) {
    result.contextImages = activeReferences.map((asset) => {
      if (!(asset.blob instanceof Blob)) fail(model, 'a reference image is unavailable. Add the original image again.');
      return asset.blob;
    });
  }
  if (guideImage) {
    if (!capabilities.supportsGuide) fail(model, 'this model cannot use a guide image. Remove it or add it as a reference to an editing model.');
    // With a reference attached the worker runs its edit graph, which has no guide input.
    if (activeReferences.length) fail(model, 'use a guide image or reference images, not both. Remove one before generating.');
    if (!(guideImage.blob instanceof Blob)) fail(model, 'the guide image is unavailable. Add the original again.');
    result.startingImage = guideImage.blob;
    result.startingImageStrength = numericValue(model, 'Guide strength', guideStrength, { min: 0, max: capabilities.maxGuideStrength, step: 0.01 });
  }
  return result;
}
